// The day's bookends: what the morning brief and the evening recap are built
// from, the copy they use (mirrored from internal/reconcile/daily.go: keep the
// two in step), and the Day record that holds the pressed wake/sleep times.
import { db, mutate } from '@/runtime'
import type { TaskResource } from '@/kinds/task/apply'
import type { DayResource } from '@/kinds/day/apply'
import type { DaySpec } from '@/kinds/day/schema'
import { isTemplate } from '@/kinds/task/schema'
import { compareToday } from '@/lib/today'
import { addDays, dateOf, formatTime } from '@/lib/time'

export interface DaySummary {
  /** finished today, newest first */
  done: TaskResource[]
  /** still on for today, in Today's order */
  left: TaskResource[]
}

export const dayOf = (tasks: TaskResource[], today: string): DaySummary => {
  const live = tasks.filter((t) => !t.deleted_at && !isTemplate(t.spec))
  const done = live
    .filter(
      (t) =>
        t.status.state === 'done' &&
        t.status.completed_at &&
        dateOf(new Date(t.status.completed_at)) === today,
    )
    .sort((a, b) => (a.status.completed_at! < b.status.completed_at! ? 1 : -1))
  const left = live
    .filter((t) => t.status.state === 'next' && !!t.spec.scheduled && t.spec.scheduled <= today)
    .sort(compareToday)
  return { done, left }
}

const plural = (n: number, one: string, many: string) => (n === 1 ? `1 ${one}` : `${n} ${many}`)
const q = (s: string) => `“${s}”`
/** Titles named, quoted and joined: “a”, “b” and “c”. */
export const joinTitles = (ts: string[]) => {
  const quoted = ts.map(q)
  if (quoted.length <= 1) return quoted.join('')
  return `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}`
}

/** The recap's headline: what is left is named, never counted. Mirrors RecapHeadline. */
export const recapHeadline = (done: number, left: string[]): { title: string; body: string } => {
  if (done === 0 && left.length === 0)
    return { title: 'Quiet day', body: 'Nothing was on for today. Tomorrow is a clean page.' }
  if (left.length === 0)
    return {
      title: 'Clean sweep',
      body: `${plural(done, 'thing', 'things')}. Everything on today's list is done.`,
    }
  if (left.length === 1) {
    if (done === 0)
      return { title: 'One to go', body: `${q(left[0]!)} is the whole of today. Small win still counts.` }
    return { title: 'So close!', body: `${plural(done, 'thing', 'things')} done. Just ${q(left[0]!)} left.` }
  }
  if (left.length <= 3)
    return {
      title: 'Nearly there',
      body: `${plural(done, 'thing', 'things')} done. ${joinTitles(left)} to go.`,
    }
  if (done === 0) return { title: 'Still time', body: `${q(left[0]!)} would be a good one to start with.` }
  return {
    title: 'Good going',
    body: `${plural(done, 'thing', 'things')} done. ${q(left[0]!)} would round it off.`,
  }
}

/** The brief's line, at most `cap` titles. Mirrors MorningLine. */
export const morningLine = (left: string[], cap: number): { title: string; body: string } => {
  const top = left.slice(0, Math.max(1, cap))
  if (top.length === 0)
    return { title: 'Morning', body: 'Today is clear. Anything on your mind before you get up?' }
  if (top.length === 1) {
    if (left.length > 1) return { title: 'Morning', body: `First up: ${q(top[0]!)}.` }
    return { title: 'Morning', body: `One thing today: ${q(top[0]!)}. Then it's yours.` }
  }
  return { title: 'Morning', body: `First up: ${q(top[0]!)}. Then ${joinTitles(top.slice(1))}.` }
}

/** Local HH:MM of a timestamp, for "Up at 07:12". */
export const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })

/** A local HH:MM setting has passed when the clock reads at or after it. */
export const hasPassed = (hhmm: string | null | undefined, now: number): boolean => {
  if (!hhmm) return false
  const d = new Date(now)
  const cur = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  return cur >= hhmm
}

/**
 * The night belongs to the day it started: a sleep pressed before 05:00 goes
 * on yesterday's record, so the brief can pair it with this morning's wake.
 */
export const nightOf = (now: number): string => {
  const d = new Date(now)
  const today = dateOf(d)
  return d.getHours() < 5 ? addDays(today, -1) : today
}

/** Find or make the Day record for a date and patch it. */
/** A Day patch: a key set to null is removed from the record (ops.ts drops it). */
export type DayPatch = { [K in keyof Omit<DaySpec, 'date'>]?: DaySpec[K] | null }

export const markDay = async (date: string, patch: DayPatch) => {
  const days = (await db.resources.where('kind').equals('Day').toArray()) as DayResource[]
  const existing = days.find((d) => !d.deleted_at && d.spec.date === date)
  if (existing) return mutate.patch(existing.id, { spec: patch })
  return mutate.create('Day', { date, ...patch } as Partial<DaySpec>, {})
}

/** "7h 32m" between lights out and waking, when both are known. */
export const nightLength = (sleptAt?: string, wokeAt?: string): string | undefined => {
  if (!sleptAt || !wokeAt) return undefined
  const mins = Math.round((Date.parse(wokeAt) - Date.parse(sleptAt)) / 60_000)
  if (mins <= 0 || mins > 20 * 60) return undefined
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h ? `${h}h${m ? ` ${m}m` : ''}` : `${m}m`
}

export const nowIso = () => formatTime(new Date())
